import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GetResultApiResponse } from '../components/RoutingResult/types';
import useApiCall from '../hooks/useApiCall';
import useGetGeneralAppData from '../hooks/useGetGeneralAppData';
import ConsultsService from '../services/ConsultsService';
import { useAppContext } from './auth';

interface IConsultsLoadingProvider {
  children: ReactNode
}

type ConsultAtStateType = {
  consultCode: number | string;
  companyCode: number | string;
  status: 'loading' | 'ready';
  resultLink: string;
  intervalId: NodeJS.Timeout;
}

type ConsultAsFunctionParam = {
  consultCode: number | string;
  companyCode: number | string;
  status: 'loading' | 'ready';
  resultLink: string;
}

interface IConsultsLoadingData {
  addConsultToast: (consult: ConsultAsFunctionParam) => void
  removeConsultToast: (consult: ConsultAsFunctionParam) => void
}

const ConsultsLoadingContext = createContext({} as IConsultsLoadingData);

export function useConsultsLoadingContext(): IConsultsLoadingData {
  const context: IConsultsLoadingData = useContext(ConsultsLoadingContext);

  return context;
}

export function ConsultsLoadingProvider({ children }: IConsultsLoadingProvider) {
  const [consults, setConsults] = useState<ConsultAtStateType[]>([]);
  const navigate = useNavigate();
  const { userCode, userProfile } = useGetGeneralAppData();
  const { isDifferentSubdomain } = useAppContext();
  const { apiCall } = useApiCall();

  const checkResultStatus = useCallback(async (consult: ConsultAsFunctionParam) => {
    const { companyCode, consultCode, resultLink } = consult;

    function updateConsultStatus(consult: ConsultAsFunctionParam) {
      setConsults((prevState) => {
        const consultBeingUpdatedIndex = prevState.findIndex((consultObj) => consultObj.consultCode === consult.consultCode);

        const prevStateClone = JSON.parse(JSON.stringify(prevState));

        prevStateClone[consultBeingUpdatedIndex] = {
          ...prevState[consultBeingUpdatedIndex],
          status: consult.status,
        };

        return prevStateClone;
      });

      const consultBeingUpdated = consults.find((cst) => cst.consultCode === consult.consultCode);

      if (consult.status === 'ready') {
        clearInterval(consultBeingUpdated?.intervalId);
      }

      function handleOpenResult() {
        navigate(consult.resultLink);
        clearInterval(consultBeingUpdated?.intervalId);
      }

      toast.update(consult.consultCode, {
        icon: consult.status === 'loading' ? () => <>🕓</> : () => <>✅</>,
        render: consult.status === 'loading'
          ? () => <div>A consulta {consult.consultCode} está sendo processada. Por favor, aguarde {!isDifferentSubdomain ? '👀': ''}</div>
          : () => <div onClick={() => handleOpenResult()}>O resultado da consulta {consult.consultCode} foi concluído! Clique aqui para acessá-lo 😁</div>
      });
    }

    await apiCall({
      apiToCall: ConsultsService.getConsultResult,
      queryParams: { consultCode, codEmpresa: companyCode, codUsu: userCode, perfilUsu: userProfile },
      actionAfterResponse: async (response: GetResultApiResponse) => {
        const { codigo, msg, emProcessamento } = response;

        if (emProcessamento) {
          updateConsultStatus({
            consultCode,
            companyCode,
            status: 'loading',
            resultLink,
          });
        } else {
          if (!codigo) {
            toast.error(`Não foi possível recuperar o status da consulta ${consultCode} (${msg})`);
            return;
          }
          updateConsultStatus({
            consultCode,
            companyCode,
            status: 'ready',
            resultLink,
          });
        }
      }
    });
  }, [apiCall, consults, isDifferentSubdomain, navigate, userCode, userProfile]);

  const addConsultToast = useCallback((consult: ConsultAsFunctionParam) => {
    setConsults((prevState) => {
      const isConsultAlreadyBeingChecked = prevState.some((cst) => cst.consultCode === consult.consultCode);

      if (isConsultAlreadyBeingChecked) {
        return prevState;
      }

      checkResultStatus(consult);

      const id = setInterval(() => {
        checkResultStatus(consult);
      }, 5000);

      function handleCloseToast() {
        clearInterval(id);
        setConsults((prevState) => (
          prevState.filter((cst) => !(cst.consultCode === consult.consultCode))
        ));
      }

      toast(<div>A consulta {consult.consultCode} está sendo processada. Por favor, aguarde {!isDifferentSubdomain ? '👀': ''}</div>, {
        toastId: consult.consultCode,
        autoClose: false,
        position: 'top-right',
        icon: consult.status === 'loading' ? () => <>🕓</> : () => <>✅</>,
        onClose: () => handleCloseToast(),
      });

      return [...prevState, {
        ...consult,
        intervalId: id,
      }];
    });
  }, [checkResultStatus, isDifferentSubdomain]);

  const removeConsultToast = useCallback((consult: ConsultAsFunctionParam) => {
    const correspondingConsult = consults.find((cst) => cst.consultCode === consult.consultCode);

    setConsults((prevState) => (prevState.filter((cst) => cst.consultCode !== consult.consultCode)));

    clearInterval(correspondingConsult?.intervalId);

    toast.dismiss(consult.consultCode);
  }, [consults]);

  const consultsLoadingData = useMemo((): IConsultsLoadingData => {

    return { addConsultToast, removeConsultToast };
  }, [addConsultToast, removeConsultToast]);

  return (
    <ConsultsLoadingContext.Provider value={consultsLoadingData}>
      {children}
    </ConsultsLoadingContext.Provider>
  );
}
